import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { DataTable, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { humanize } from '../../components/StatusChip';
import { businessDate } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { DocumentCell } from './LedgerTab';
import { contactKeys, type Contact, type Ledger } from './model';

type OpenItem = Schemas['OpenItems']['claims'][number];

const LABEL: Record<string, string> = {
  OPENING_BALANCE: 'Opening balance',
  SALES_INVOICE: 'Sales invoice',
  SUPPLIER_INVOICE: 'Supplier bill',
  PAYMENT: 'Payment',
  CREDIT_NOTE: 'Credit note',
  DEBIT_NOTE: 'Debit note',
};

/** Outstanding documents (claims) and unallocated credit for one ledger. */
export function OpenItemsTab({ contact, ledger }: { contact: Contact; ledger: Ledger }) {
  const { format } = useShop();
  const data = useQuery({
    queryKey: contactKeys.openItems(contact.id, ledger),
    queryFn: () =>
      unwrap(
        api.GET('/contacts/{id}/open-items', {
          params: { path: { id: contact.id }, query: { ledger } },
        }),
      ),
  });

  const columns = (credit: boolean): Column<OpenItem>[] => [
    {
      key: 'doc',
      header: 'Document',
      render: (i) => (
        <DocumentCell
          doc={{
            type: i.type,
            id: i.id,
            label: LABEL[i.type] ?? humanize(i.type),
            number: i.number,
            reference: null,
          }}
        />
      ),
    },
    {
      key: 'date',
      header: 'Date',
      hideBelow: 'sm',
      render: (i) => businessDate(i.date, format),
    },
    {
      key: 'due',
      header: 'Due',
      hideBelow: 'md',
      render: (i) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{i.dueDate ? businessDate(i.dueDate, format) : '—'}</span>
          {i.overdue && (
            <Chip size="small" color="error" label="Overdue" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Stack>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      hideBelow: 'md',
      render: (i) => <Money value={i.amount} />,
    },
    {
      key: 'progress',
      header: credit ? 'Used' : 'Paid',
      hideBelow: 'lg',
      width: 160,
      render: (i) => {
        const pct = Number(i.amount) > 0 ? (Number(i.allocated) / Number(i.amount)) * 100 : 0;
        return (
          <Box>
            <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" className="tabular">
              <Money value={i.allocated} />
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'open',
      header: credit ? 'Available' : 'Open',
      align: 'right',
      render: (i) => <Money value={i.open} strong />,
    },
  ];

  const d = data.data;
  return (
    <Stack spacing={2.5}>
      <DataTable
        columns={columns(false)}
        rows={d?.claims}
        rowKey={(i) => `${i.type}:${i.id}`}
        loading={data.isLoading}
        toolbar={
          <Stack
            direction="row"
            sx={{ flex: 1, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="h6">Outstanding documents</Typography>
              <Typography variant="caption" color="text.secondary">
                {ledger === 'RECEIVABLE'
                  ? 'What this customer still has to pay'
                  : 'What you still have to pay this supplier'}
              </Typography>
            </Box>
            {d && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="overline" color="text.secondary">
                  Total open
                </Typography>
                <Typography variant="h5" className="tabular">
                  <Money value={d.totalOutstanding} />
                </Typography>
              </Box>
            )}
          </Stack>
        }
        empty={
          <EmptyState
            icon={<TaskAltRounded />}
            title="All settled"
            body={`${contact.name} has no open ${ledger === 'RECEIVABLE' ? 'invoices' : 'bills'} on this ledger.`}
          />
        }
      />
      {d && d.credits.length > 0 && (
        <DataTable
          columns={columns(true)}
          rows={d.credits}
          rowKey={(i) => `${i.type}:${i.id}`}
          toolbar={
            <Stack
              direction="row"
              sx={{ flex: 1, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="h6">Unallocated credit</Typography>
                <Typography variant="caption" color="text.secondary">
                  Payments and notes not yet applied to a document
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="overline" color="text.secondary">
                  Total credit
                </Typography>
                <Typography variant="h5" className="tabular" color="success.main">
                  <Money value={d.totalCredit} />
                </Typography>
              </Box>
            </Stack>
          }
        />
      )}
    </Stack>
  );
}
